import React, { useEffect, useRef, useState } from "react";
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from "@mui/material";

import { IPD_COLORS, ipdElevatedInputSx } from "../../ipd/utils";
import { searchMedicalServices } from "../api/emergencyApi";

const DEBOUNCE_MS = 300;

/**
 * Type-to-search picker for the test or scan an investigation order names,
 * backed by the hospital's service catalogue (`search_medical_services/`).
 *
 * Deliberately NOT free-solo, unlike the medicine picker beside it. A medicine
 * outside the formulary can still be given, so that one accepts free text. An
 * investigation cannot: without a catalogue service the order gets no
 * LAB-/RAD- number, reaches neither department's worklist, and its result can
 * never be matched back to the visit. Free text there would produce an order
 * that looks placed and is lost, which is worse than one that cannot be
 * submitted.
 *
 * `value` is the chosen service ({ medicalServiceId, term }) or null; `onChange`
 * reports the whole service so the caller keeps both the id and the name.
 */
export default function EmergencyServiceAutocomplete({ value, onChange, disabled }) {
  const [input, setInput] = useState(value?.term || "");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const reqRef = useRef(0);

  // Keep the visible text in step when the parent clears or replaces the line
  // (submitting the basket resets every line to blank).
  useEffect(() => {
    setInput(value?.term || "");
  }, [value?.term]);

  useEffect(() => {
    const term = (input || "").trim();
    if (term.length < 2 || term === value?.term) {
      setOptions([]);
      return undefined;
    }
    const reqId = ++reqRef.current;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const rows = await searchMedicalServices(term);
        if (reqRef.current === reqId) setOptions(rows);
      } catch {
        if (reqRef.current === reqId) setOptions([]);
      } finally {
        if (reqRef.current === reqId) setLoading(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [input, value?.term]);

  return (
    <Autocomplete
      size="small"
      disabled={disabled}
      options={options}
      loading={loading}
      value={value || null}
      inputValue={input}
      // The catalogue already ranks the results; re-filtering by label here
      // would hide a service the server considered the best match.
      filterOptions={(x) => x}
      getOptionLabel={(o) => o?.term || ""}
      isOptionEqualToValue={(a, b) => a?.medicalServiceId === b?.medicalServiceId}
      onInputChange={(_, v, reason) => {
        if (reason !== "reset") setInput(v);
      }}
      onChange={(_, v) => {
        onChange(v || null);
        setInput(v?.term || "");
      }}
      renderOption={(props, o) => {
        const isRadiology =
          o.serviceGroupId === 1 ||
          (o.serviceType && String(o.serviceType).toUpperCase().includes("RAD"));
        const isLab =
          o.serviceGroupId === 10 ||
          (!isRadiology && o.serviceGroupId != null);
        const deptLabel = isRadiology ? "RAD" : isLab ? "LAB" : "NO DEPT";
        const deptColor = isRadiology
          ? { bg: "#e0f2fe", text: "#0369a1", border: "#bae6fd" }
          : isLab
          ? { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0" }
          : { bg: "#fef2f2", text: "#b91c1c", border: "#fecaca" };

        return (
          <li {...props} key={o.medicalServiceId}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                py: 0.5,
              }}
            >
              <Box sx={{ pr: 1 }}>
                <Typography sx={{ fontSize: 13.5 }}>{o.term}</Typography>
                {o.serviceType && (
                  <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
                    {o.serviceType}
                  </Typography>
                )}
              </Box>
              <Chip
                size="small"
                label={deptLabel}
                sx={{
                  height: 20,
                  fontSize: 10,
                  fontWeight: 700,
                  backgroundColor: deptColor.bg,
                  color: deptColor.text,
                  border: `1px solid ${deptColor.border}`,
                  flexShrink: 0,
                }}
              />
            </Box>
          </li>
        );
      }}
      noOptionsText={loading ? "Searching…" : "Type to search tests and scans"}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Search the test or scan…"
          sx={ipdElevatedInputSx}
          inputProps={{ ...params.inputProps, "aria-label": "What is being ordered" }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={15} sx={{ mr: 1 }} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
